/**
 * Public embed: /embed/access-configurator?token=xxx
 * Category picker + access categories; residential_garage bridges to original sectional UI
 * (ConfiguratorLayout) without modifying EmbedConfigurator / ConfiguratorLayout sources.
 */

import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AccessConfiguratorShell from "@/components/access-configurator/AccessConfiguratorShell";
import type { AccessFormValues } from "@/components/access-configurator/AccessForm";
import type {
  AccessConfiguratorOptions,
  AccessRogerSeriesRule,
} from "@shared/access-configurator";
import type {
  ConfiguratorFormValues,
  ConfiguratorProductOption,
  PriceCombination,
} from "@/components/price-generator/ConfiguratorForm";
import { reportConfiguratorConversion } from "@/lib/google-ads-conversion";
import { apiUrl } from "@/lib/api-client";

interface SubmitResult {
  success: boolean;
  dealId?: string;
  pricingMode?: "priced" | "lead_only";
  resultType?: string;
  error?: string;
  source?: "access" | "sectional";
  quoteSummary?: {
    email: string;
    categoryId?: string;
    width: number;
    height: number;
    color?: string;
    motif?: string;
    surface?: string;
    cassette?: string;
    rogerSeries?: string;
    swingPattern?: string;
    swingFrame?: string;
    highSpeedFrame?: string;
    highSpeedGlazing?: string;
    barrierModel?: string;
    barrierFinish?: string;
    barrierLed?: boolean;
    barrierSkirt?: boolean;
    barrierSupport?: string;
    electricDrive?: boolean;
    bayCount?: number;
    dockLines?: string[];
    dockNote?: string;
  };
}

function ResultScreen({ result, onReset }: { result: SubmitResult; onReset: () => void }) {
  const { t } = useTranslation();
  if (!result.success) {
    return (
      <div className="access-configurator-layout configurator-layout flex flex-col items-center justify-center min-h-[100dvh] px-6 text-center bg-background">
        <h2 className="mt-6 text-2xl font-bold">{t("accessConfigurator.resultErrorTitle")}</h2>
        <p className="mt-3 text-sm text-muted-foreground max-w-md">{result.error}</p>
        <button
          type="button"
          onClick={onReset}
          className="configurator-btn-primary mt-8 px-8 py-3 text-sm font-semibold rounded-md text-white"
        >
          {t("accessConfigurator.resultNewQuote")}
        </button>
      </div>
    );
  }

  const summary = result.quoteSummary;
  const isSectional = result.source === "sectional";
  return (
    <div className="access-configurator-layout configurator-layout flex flex-col items-center justify-center min-h-[100dvh] px-6 text-center bg-background">
      <h2 className="mt-6 text-2xl font-bold">{t("accessConfigurator.resultTitle")}</h2>
      <p className="mt-3 text-[15px] leading-relaxed text-foreground/80 max-w-md">
        {result.pricingMode === "lead_only"
          ? t("accessConfigurator.resultLeadOnly")
          : t("accessConfigurator.resultSuccess")}
      </p>
      {summary && (
        <div className="mt-4 w-full max-w-sm text-left rounded-lg border border-border bg-muted/30 px-4 py-3">
          <ul className="space-y-1.5 text-sm">
            <li>
              <span className="text-muted-foreground">{t("accessConfigurator.resultSentTo")} </span>
              {summary.email}
            </li>
            <li>
              <span className="text-muted-foreground">{t("accessConfigurator.resultCategory")} </span>
              {t(
                `accessConfigurator.categories.${summary.categoryId ?? "residential_garage"}`,
                summary.categoryId ?? "residential_garage"
              )}
            </li>
            {summary.categoryId === "loading_dock" ? (
              <>
                <li>
                  <span className="text-muted-foreground">{t("accessConfigurator.loadingDock.bayCount")} </span>
                  {summary.bayCount ?? 1}
                </li>
                {(summary.dockLines ?? []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
                {summary.dockNote ? (
                  <li>
                    <span className="text-muted-foreground">{t("accessConfigurator.loadingDock.note")} </span>
                    {summary.dockNote}
                  </li>
                ) : null}
              </>
            ) : (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.resultSize")} </span>
                {summary.width} × {summary.height} mm
              </li>
            )}
            {summary.color && (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.color")} </span>
                {summary.color}
              </li>
            )}
            {summary.motif && (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.motif")} </span>
                {isSectional
                  ? summary.motif === "m" || summary.motif === "1"
                    ? "Prelis M"
                    : summary.motif === "l" || summary.motif === "2"
                      ? "Prelis L"
                      : summary.motif
                  : t(`accessConfigurator.motifs.${summary.motif}`, summary.motif)}
                {summary.surface && !isSectional
                  ? ` · ${t(`accessConfigurator.surfaces.${summary.surface}`, summary.surface)}`
                  : ""}
                {summary.surface && isSectional ? ` · ${summary.surface}` : ""}
              </li>
            )}
            {summary.cassette && (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.cassette")} </span>
                {t(`accessConfigurator.cassettes.${summary.cassette}`, summary.cassette)}
              </li>
            )}
            {summary.swingPattern && (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.pattern")} </span>
                {t(`accessConfigurator.swingPatterns.${summary.swingPattern}`, summary.swingPattern)}
                {summary.swingFrame
                  ? ` · ${t(`accessConfigurator.swingFrames.${summary.swingFrame}`, summary.swingFrame)}`
                  : ""}
              </li>
            )}
            {summary.highSpeedFrame && (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.frame")} </span>
                {t(`accessConfigurator.highSpeedFrames.${summary.highSpeedFrame}`, summary.highSpeedFrame)}
                {summary.highSpeedGlazing
                  ? ` · ${t(`accessConfigurator.highSpeedGlazings.${summary.highSpeedGlazing}`, summary.highSpeedGlazing)}`
                  : ""}
              </li>
            )}
            {summary.barrierModel && (
              <li>
                <span className="text-muted-foreground">{t("accessConfigurator.barrierModel")} </span>
                {summary.barrierModel}
                {summary.barrierFinish
                  ? ` · ${t(`accessConfigurator.barrierFinishes.${summary.barrierFinish}`, summary.barrierFinish)}`
                  : ""}
              </li>
            )}
            {summary.rogerSeries && (
              <li>
                <span className="text-muted-foreground">Roger: </span>
                {summary.rogerSeries}
              </li>
            )}
            {typeof summary.electricDrive === "boolean" && (
              <li>
                <span className="text-muted-foreground">{t("configurator.electricDrive")}: </span>
                {summary.electricDrive ? t("configurator.resultYes") : t("configurator.resultNo")}
              </li>
            )}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={onReset}
        className="configurator-btn-primary mt-8 px-8 py-3 text-sm font-semibold rounded-md text-white"
      >
        {t("accessConfigurator.resultNewQuote")}
      </button>
    </div>
  );
}

export default function EmbedAccessConfigurator() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get("token")?.trim() ?? undefined;
  const [options, setOptions] = useState<AccessConfiguratorOptions | null>(null);
  const [rogerRules, setRogerRules] = useState<AccessRogerSeriesRule[] | null>(null);
  const [sectionalOptions, setSectionalOptions] = useState<ConfiguratorProductOption[]>([]);
  const [sectionalPriceCombinations, setSectionalPriceCombinations] = useState<PriceCombination[]>(
    []
  );
  const [sectionalMotivOrder, setSectionalMotivOrder] = useState<string[] | undefined>(undefined);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [shellKey, setShellKey] = useState(0);

  useEffect(() => {
    const langParam = searchParams.get("lang")?.toLowerCase();
    const pathHasHu = location.pathname.includes("/hu/");
    i18n.changeLanguage(pathHasHu || langParam === "hu" ? "hu" : "sk");
  }, [location.pathname, searchParams, i18n]);

  useEffect(() => {
    fetch(apiUrl("/api/public/access-configurator/options"))
      .then((r) => r.json())
      .then((data) => {
        setOptions(data.accessConfiguratorOptions ?? null);
        setRogerRules(Array.isArray(data.accessRogerSeriesRules) ? data.accessRogerSeriesRules : null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(apiUrl("/api/public/configurator/options"))
      .then((r) => r.json())
      .then((data) => {
        setSectionalOptions(
          Array.isArray(data.configuratorProductOptions) ? data.configuratorProductOptions : []
        );
        setSectionalPriceCombinations(
          Array.isArray(data.priceCombinations) ? data.priceCombinations : []
        );
        if (Array.isArray(data.motivOrder) && data.motivOrder.length > 0) {
          setSectionalMotivOrder(data.motivOrder);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("embed-configurator-active");
    return () => {
      document.documentElement.classList.remove("embed-configurator-active");
    };
  }, []);

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] text-sm text-muted-foreground">
        {t("accessConfigurator.missingToken")}
      </div>
    );
  }

  if (submitResult) {
    return (
      <ResultScreen
        result={submitResult}
        onReset={() => {
          setSubmitResult(null);
          setShellKey((k) => k + 1);
        }}
      />
    );
  }

  const handleSubmitAccess = async (values: AccessFormValues) => {
    setSubmitting(true);
    try {
      // Header + query fallback: CORS/proxies may strip custom headers; server accepts both.
      const postUrl = apiUrl(
        `/api/public/access-configurator?token=${encodeURIComponent(token)}`
      );
      const res = await fetch(postUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Access-Configurator-Token": token,
          "X-Configurator-Token": token,
        },
        body: JSON.stringify({
          categoryId: values.categoryId,
          gateVariant: values.gateVariant,
          width: values.width,
          height: values.height,
          color: values.color || undefined,
          colorHex: values.colorHex || undefined,
          visualVariant: values.visualVariant,
          motif: values.categoryId === "industrial" ? values.motif : undefined,
          surface: values.categoryId === "industrial" ? values.surface : undefined,
          cassette: values.categoryId === "roller" ? values.cassette : undefined,
          swingPattern: values.categoryId === "swing" ? values.swingPattern : undefined,
          swingFrame: values.categoryId === "swing" ? values.swingFrame : undefined,
          highSpeedFrame: values.categoryId === "high_speed" ? values.highSpeedFrame : undefined,
          highSpeedGlazing: values.categoryId === "high_speed" ? values.highSpeedGlazing : undefined,
          barrierModel: values.categoryId === "barrier" ? values.barrierModel : undefined,
          barrierFinish: values.categoryId === "barrier" ? values.barrierFinish : undefined,
          barrierLed: values.categoryId === "barrier" ? values.barrierLed : undefined,
          barrierSkirt: values.categoryId === "barrier" ? values.barrierSkirt : undefined,
          barrierSupport: values.categoryId === "barrier" ? values.barrierSupport : undefined,
          dockLeveller: values.categoryId === "loading_dock" ? values.dockLeveller : undefined,
          dockLevellerType: values.categoryId === "loading_dock" ? values.dockLevellerType : undefined,
          dockSeal: values.categoryId === "loading_dock" ? values.dockSeal : undefined,
          dockSealType: values.categoryId === "loading_dock" ? values.dockSealType : undefined,
          dockBumpers: values.categoryId === "loading_dock" ? values.dockBumpers : undefined,
          dockDoor: values.categoryId === "loading_dock" ? values.dockDoor : undefined,
          bayCount: values.categoryId === "loading_dock" ? values.bayCount : undefined,
          automationWanted: values.automationWanted,
          automationMount: values.automationMount,
          leafCount: values.leafCount,
          weightKg: values.weightKg,
          rogerSeries: values.categoryId === "loading_dock" ? undefined : values.rogerSeries || undefined,
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
          city: values.city || undefined,
          address: values.address || undefined,
          country: values.country || undefined,
          message: values.message || undefined,
          chcemNaFirmu: values.chcemNaFirmu,
          ico: values.ico || undefined,
          obchodneMeno: values.obchodneMeno || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.dealId) {
        setSubmitResult({
          success: true,
          dealId: data.dealId,
          pricingMode: data.pricingMode ?? "lead_only",
          resultType: data.resultType,
          source: "access",
          quoteSummary: {
            email: values.email,
            categoryId: values.categoryId,
            width: values.width,
            height: values.height,
            color: values.color || undefined,
            motif: values.categoryId === "industrial" ? values.motif : undefined,
            surface: values.categoryId === "industrial" ? values.surface : undefined,
            cassette: values.categoryId === "roller" ? values.cassette : undefined,
            swingPattern: values.categoryId === "swing" ? values.swingPattern : undefined,
            swingFrame: values.categoryId === "swing" ? values.swingFrame : undefined,
            highSpeedFrame: values.categoryId === "high_speed" ? values.highSpeedFrame : undefined,
            highSpeedGlazing: values.categoryId === "high_speed" ? values.highSpeedGlazing : undefined,
            barrierModel: values.categoryId === "barrier" ? values.barrierModel : undefined,
            barrierFinish: values.categoryId === "barrier" ? values.barrierFinish : undefined,
            barrierLed: values.categoryId === "barrier" ? values.barrierLed : undefined,
            barrierSkirt: values.categoryId === "barrier" ? values.barrierSkirt : undefined,
            barrierSupport: values.categoryId === "barrier" ? values.barrierSupport : undefined,
            rogerSeries: values.categoryId === "loading_dock" ? undefined : values.rogerSeries || undefined,
            bayCount: values.categoryId === "loading_dock" ? values.bayCount : undefined,
            dockNote: values.categoryId === "loading_dock" ? values.message || undefined : undefined,
            dockLines:
              values.categoryId === "loading_dock"
                ? [
                    values.dockLeveller
                      ? `${t("accessConfigurator.loadingDock.items.leveller")}: ${
                          values.dockLevellerType === "advise"
                            ? t("accessConfigurator.loadingDock.advise")
                            : t(`accessConfigurator.loadingDock.levellerTypes.${values.dockLevellerType}`)
                        }`
                      : null,
                    values.dockSeal
                      ? `${t("accessConfigurator.loadingDock.items.seal")}: ${
                          values.dockSealType === "advise"
                            ? t("accessConfigurator.loadingDock.advise")
                            : t(`accessConfigurator.loadingDock.sealTypes.${values.dockSealType}`)
                        }`
                      : null,
                    values.dockBumpers
                      ? `${t("accessConfigurator.loadingDock.items.bumpers")} (${t("accessConfigurator.loadingDock.bumpersPair")})`
                      : null,
                    values.dockDoor ? t("accessConfigurator.loadingDock.items.door") : null,
                  ].filter((line): line is string => Boolean(line))
                : undefined,
          },
        });
      } else {
        setSubmitResult({
          success: false,
          error: data.message ?? data.error ?? `HTTP ${res.status}`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSectional = async (values: ConfiguratorFormValues, submitToken: string) => {
    setSubmitResult(null);
    try {
      const res = await fetch(apiUrl("/api/public/configurator"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Configurator-Token": submitToken,
        },
        body: JSON.stringify({
          width: values.width,
          height: values.height,
          motiv: values.motiv,
          surface: values.surface,
          color: values.color || undefined,
          electricDrive: values.electricDrive,
          address: values.address || undefined,
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
          city: values.city || undefined,
          country: values.country || undefined,
          message: values.message || undefined,
          chcemNaFirmu: values.chcemNaFirmu === true,
          obchodneMeno: values.obchodneMeno?.trim() || undefined,
          ico: values.ico || undefined,
          dic: values.dic || undefined,
          icDph: values.icDph || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.dealId) {
        reportConfiguratorConversion(values);
        setSubmitResult({
          success: true,
          dealId: data.dealId,
          resultType: data.resultType ?? "custom_required",
          pricingMode: "priced",
          source: "sectional",
          quoteSummary: {
            email: values.email,
            categoryId: "residential_garage",
            width: values.width,
            height: values.height,
            surface: values.surface || undefined,
            color: values.color || undefined,
            motif: values.motiv || undefined,
            electricDrive: values.electricDrive,
          },
        });
      } else {
        const messageFromEmailArray = Array.isArray(data.email) ? data.email[0] : null;
        const errorMessage =
          data.error === "invalid_email"
            ? t("configurator.errorInvalidEmail")
            : data.error === "missing_params"
              ? data.message || t("configurator.errorMissingParams")
              : messageFromEmailArray ?? data.message ?? data.error ?? `HTTP ${res.status}`;
        setSubmitResult({
          success: false,
          error: errorMessage,
        });
      }
    } catch (err) {
      setSubmitResult({
        success: false,
        error: err instanceof Error ? err.message : "Request failed",
      });
    }
  };

  return (
    <div className="h-full min-h-0 overflow-hidden flex flex-col bg-background text-foreground">
      <div className="flex-1 min-h-0 w-full mx-auto flex flex-col">
        <AccessConfiguratorShell
          key={shellKey}
          options={options}
          rogerRules={rogerRules}
          token={token}
          embedMode
          sectionalProductOptions={sectionalOptions}
          sectionalPriceCombinations={sectionalPriceCombinations}
          sectionalMotivOrder={sectionalMotivOrder}
          onSubmit={handleSubmitAccess}
          onSubmitSectional={handleSubmitSectional}
          submitting={submitting}
        />
      </div>
    </div>
  );
}
